import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Rocket, Plus, Trash2, Upload, ArrowUp, ArrowDown, Image as ImageIcon, Eye, EyeOff, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

const ANALYTICS = [["ga4", "GA4 (G-XXXX)"], ["gtm", "GTM (GTM-XXXX)"], ["meta_pixel", "Meta Pixel ID"], ["google_ads", "Google Ads (AW-XXXX)"], ["tiktok_pixel", "TikTok Pixel ID"]];
const SEC_LABELS = { problema: "Problemas", split: "Dois caminhos (músico/contratante)", finance: "Financeiro", repertoire: "Repertório", how: "Como funciona", score: "Score", recursos: "Recursos", planos: "Planos", seguranca: "Segurança / Prova social", faq: "FAQ" };
const ALL_SECTIONS = Object.keys(SEC_LABELS);
const ICON_NAMES = ["MessageSquareOff", "CalendarClock", "Wallet", "Library", "Trophy", "Radio", "MapPin", "BarChart3", "Bell", "Repeat", "Zap", "CreditCard", "ShieldCheck", "Share2", "Users", "Star", "Music4", "Guitar", "Sparkles", "CheckCircle2", "UserPlus", "ListChecks", "PlayCircle"];

export default function AdminLanding() {
  const [c, setC] = useState(null);
  const [plans, setPlans] = useState([]);
  const [uploading, setUploading] = useState("");
  const [status, setStatus] = useState(null);
  const fileRefs = useRef({});
  const s = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const loadStatus = () => api.get("/admin/landing/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/landing").then((r) => setC(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    loadStatus();
  }, []);

  const saveDraft = async (silent) => {
    try { await api.put("/admin/landing", c); if (!silent) toast.success("Rascunho salvo!"); loadStatus(); return true; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; }
  };
  const publish = async () => {
    if (!(await saveDraft(true))) return;
    try { await api.post("/admin/landing/publish"); toast.success("Landing publicada!"); loadStatus(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const discard = async () => {
    try { const r = await api.post("/admin/landing/discard"); setC(r.data); toast.success("Rascunho descartado."); loadStatus(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const preview = async (path) => { await saveDraft(true); window.open(path, "_blank"); };

  const upload = async (key, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(key);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); s(key, r.data.url); toast.success("Imagem enviada!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(""); e.target.value = ""; }
  };

  if (!c) return <div className="text-zinc-500">Carregando...</div>;
  const inp = "bg-[#09090B] border-white/10 rounded-xl mt-1";
  const cardCls = "bg-card border border-white/[0.06] rounded-xl p-4 space-y-3";
  const secTitle = (txt) => <div className="text-xs uppercase tracking-widest text-zinc-500">{txt}</div>;

  const order = (c.section_order && c.section_order.length ? c.section_order : ALL_SECTIONS);
  const displayOrder = [...order.filter((x) => ALL_SECTIONS.includes(x)), ...ALL_SECTIONS.filter((x) => !order.includes(x))];
  const secVisible = (id) => (c.sections || {})[id] !== false;
  const moveSection = (i, dir) => {
    const arr = [...displayOrder]; const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; s("section_order", arr);
  };
  const toggleSection = (id) => s("sections", { ...(c.sections || {}), [id]: !secVisible(id) });

  // ---- reusable field renderers ----
  const ImageField = ({ label, k, aspect = "aspect-video", contain = false }) => (
    <div>
      <Label className="text-[11px] text-zinc-500">{label}</Label>
      <div className={`mt-1 relative ${aspect} rounded-xl overflow-hidden border border-white/10 bg-[#09090B] flex items-center justify-center`}>
        {c[k] ? <img src={asset(c[k])} alt={label} className={`w-full h-full ${contain ? "object-contain p-2" : "object-cover"}`} /> : <ImageIcon className="h-6 w-6 text-zinc-700" />}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input ref={(el) => (fileRefs.current[k] = el)} type="file" accept="image/*" className="hidden" data-testid={`lp-upload-${k}`} onChange={(e) => upload(k, e)} />
        <button onClick={() => fileRefs.current[k]?.click()} disabled={uploading === k} className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline disabled:opacity-50"><Upload className="h-3.5 w-3.5" />{uploading === k ? "Enviando..." : "Enviar imagem"}</button>
        {c[k] && <button onClick={() => s(k, "")} data-testid={`lp-remove-${k}`} className="text-xs text-zinc-500 hover:text-red-400">Remover</button>}
      </div>
    </div>
  );

  const iconTextList = (k, addLabel) => {
    const items = c[k] || [];
    return (
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-[#09090B] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input list="lp-icons" value={it.icon || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], icon: e.target.value }; s(k, n); }} placeholder="Ícone (ex: Wallet)" className="bg-card border-white/10 rounded-lg h-8 text-xs w-40" />
              <Input value={it.t || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], t: e.target.value }; s(k, n); }} placeholder="Título" className="bg-card border-white/10 rounded-lg h-8 text-sm flex-1" />
              <button onClick={() => s(k, items.filter((_, j) => j !== i))} className="h-8 w-8 rounded-lg border border-white/10 text-zinc-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <Textarea value={it.d || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], d: e.target.value }; s(k, n); }} placeholder="Descrição" className="bg-card border-white/10 rounded-lg text-sm min-h-[52px]" />
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1" onClick={() => s(k, [...items, { icon: "Sparkles", t: "", d: "" }])}><Plus className="h-3.5 w-3.5" />{addLabel}</Button>
        {items.length === 0 && <p className="text-[11px] text-zinc-600">Sem itens — a landing usa o conteúdo padrão.</p>}
      </div>
    );
  };

  const tdList = (k, addLabel) => {
    const items = c[k] || [];
    return (
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-2 text-xs text-zinc-500 w-5 shrink-0">{i + 1}.</span>
            <div className="flex-1 space-y-1">
              <Input value={it.t || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], t: e.target.value }; s(k, n); }} placeholder="Título do passo" className="bg-[#09090B] border-white/10 rounded-lg h-8 text-sm" />
              <Input value={it.d || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], d: e.target.value }; s(k, n); }} placeholder="Descrição" className="bg-[#09090B] border-white/10 rounded-lg h-8 text-sm" />
            </div>
            <button onClick={() => s(k, items.filter((_, j) => j !== i))} className="h-8 w-8 rounded-lg border border-white/10 text-zinc-500 hover:text-red-400 flex items-center justify-center shrink-0 mt-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1" onClick={() => s(k, [...items, { t: "", d: "" }])}><Plus className="h-3.5 w-3.5" />{addLabel}</Button>
        {items.length === 0 && <p className="text-[11px] text-zinc-600">Sem itens — a landing usa o conteúdo padrão.</p>}
      </div>
    );
  };

  const strList = (k, addLabel) => {
    const items = c[k] || [];
    return (
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={it} onChange={(e) => { const n = [...items]; n[i] = e.target.value; s(k, n); }} placeholder="Benefício" className="bg-[#09090B] border-white/10 rounded-lg h-8 text-sm flex-1" />
            <button onClick={() => s(k, items.filter((_, j) => j !== i))} className="h-8 w-8 rounded-lg border border-white/10 text-zinc-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1" onClick={() => s(k, [...items, ""])}><Plus className="h-3.5 w-3.5" />{addLabel}</Button>
        {items.length === 0 && <p className="text-[11px] text-zinc-600">Sem itens — a landing usa o conteúdo padrão.</p>}
      </div>
    );
  };

  const txt = (k, label, area = false) => (
    <div><Label className="text-[11px] text-zinc-500">{label}</Label>
      {area ? <Textarea data-testid={`lp-input-${k}`} value={c[k] || ""} onChange={(e) => s(k, e.target.value)} className={inp} /> : <Input data-testid={`lp-input-${k}`} value={c[k] || ""} onChange={(e) => s(k, e.target.value)} className={inp} />}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-landing">
      <datalist id="lp-icons">{ICON_NAMES.map((n) => <option key={n} value={n} />)}</datalist>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur py-2 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-head text-xl font-semibold flex items-center gap-2"><Rocket className="h-5 w-5 text-[#FF4D00]" />Landing Page
            {status?.has_draft && <span data-testid="draft-badge" className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">Rascunho não publicado</span>}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="preview-landing" variant="outline" onClick={() => preview("/preview/landing")} className="rounded-xl bg-transparent border-white/10 gap-1.5"><ExternalLink className="h-4 w-4" />Preview landing</Button>
            <Button data-testid="preview-login" variant="outline" onClick={() => preview("/preview/login")} className="rounded-xl bg-transparent border-white/10 gap-1.5"><ExternalLink className="h-4 w-4" />Preview login</Button>
            <Button data-testid="landing-save" onClick={() => saveDraft(false)} variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1.5"><Save className="h-4 w-4" />Salvar rascunho</Button>
            <Button data-testid="landing-publish" onClick={publish} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1.5"><Rocket className="h-4 w-4" />Publicar</Button>
          </div>
        </div>
        {status?.has_draft && <button data-testid="landing-discard" onClick={discard} className="text-xs text-zinc-500 hover:text-red-400">Descartar rascunho e voltar ao publicado</button>}
      </div>

      {/* LOGO & LOGOMARCA DA LANDING */}
      <div className={cardCls}>
        {secTitle("Logo & Logomarca da Landing")}
        <p className="text-[11px] text-zinc-600">Vazio = usa a logo do Aplicativo (Identidade Visual). A logomarca completa, se enviada, substitui o símbolo + nome "{"Mí Chamou"}" no topo e no rodapé.</p>
        <div className="grid grid-cols-2 gap-3">
          <ImageField label="Logo / símbolo" k="img_logo" aspect="aspect-square" contain />
          <ImageField label="Logomarca completa (símbolo + texto)" k="img_logomarca" aspect="aspect-[3/1]" contain />
          <ImageField label="Favicon (ícone da aba do navegador)" k="favicon" aspect="aspect-square" contain />
        </div>
      </div>

      {/* TELA DE LOGIN */}
      <div className={cardCls}>
        {secTitle("Tela de Login / Cadastro")}
        <p className="text-[11px] text-zinc-600">Personalize o painel lateral da tela de login/cadastro. Imagem vazia = usa a imagem padrão.</p>
        {txt("auth_title", "Título do painel")}
        {txt("auth_subtitle", "Subtítulo do painel", true)}
        <ImageField label="Imagem da tela de login" k="auth_image" />
      </div>

      {/* HERO */}
      <div className={cardCls}>
        {secTitle("Hero & CTAs")}
        {txt("hero_badge", "Selo")}
        {txt("hero_title", "Título")}
        {txt("hero_subtitle", "Subtítulo", true)}
        {txt("hero_note", "Complemento")}
        <div className="grid grid-cols-2 gap-2">{txt("cta_musico", "CTA músico")}{txt("cta_contratante", "CTA contratante")}</div>
        <ImageField label="Imagem do Hero" k="img_hero" />
      </div>

      {/* ORDEM & VISIBILIDADE */}
      <div className={cardCls}>
        {secTitle("Seções — ordem & visibilidade")}
        <div className="space-y-2">
          {displayOrder.map((id, i) => (
            <div key={id} className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#09090B] px-3 py-2" data-testid={`lp-section-${id}`}>
              <span className="text-xs text-zinc-500 w-5">{i + 1}</span>
              <span className={`flex-1 text-sm ${secVisible(id) ? "text-zinc-200" : "text-zinc-600 line-through"}`}>{SEC_LABELS[id]}</span>
              <button onClick={() => toggleSection(id)} title="Mostrar/ocultar" className="h-7 w-7 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-accent" data-testid={`lp-toggle-${id}`}>{secVisible(id) ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
              <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="h-7 w-7 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-accent disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => moveSection(i, 1)} disabled={i === displayOrder.length - 1} className="h-7 w-7 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-accent disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* PROBLEMAS */}
      <div className={cardCls}>
        {secTitle("Seção Problemas")}
        {txt("problema_title", "Título")}
        {txt("problema_sub", "Subtítulo")}
        {txt("problema_footer", "Frase de fechamento")}
        <Label className="text-[11px] text-zinc-500 block pt-1">Cards de problemas</Label>
        {iconTextList("problems", "Problema")}
      </div>

      {/* DOIS CAMINHOS */}
      <div className={cardCls}>
        {secTitle("Seção Dois Caminhos")}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-[11px] font-semibold text-accent">Criadores de eventos</div>
            {txt("split_creators_kicker", "Rótulo")}
            {txt("split_creators_title", "Título")}
            {txt("split_creators_cta", "Texto do botão")}
            <Label className="text-[11px] text-zinc-500 block pt-1">Benefícios</Label>
            {strList("creator_benefits", "Benefício")}
            <ImageField label="Imagem (criadores)" k="img_creators" aspect="aspect-[4/3]" />
          </div>
          <div className="space-y-2">
            <div className="text-[11px] font-semibold text-accent">Músicos</div>
            {txt("split_musicians_kicker", "Rótulo")}
            {txt("split_musicians_title", "Título")}
            {txt("split_musicians_cta", "Texto do botão")}
            <Label className="text-[11px] text-zinc-500 block pt-1">Benefícios</Label>
            {strList("musician_benefits", "Benefício")}
            <ImageField label="Imagem (músicos)" k="img_musicians" aspect="aspect-[4/3]" />
          </div>
        </div>
      </div>

      {/* FINANCEIRO */}
      <div className={cardCls}>
        {secTitle("Seção Financeiro")}
        {txt("finance_kicker", "Rótulo")}
        {txt("finance_title", "Título")}
        {txt("finance_desc", "Descrição", true)}
        <ImageField label="Imagem (financeiro)" k="img_finance" />
      </div>

      {/* REPERTÓRIO */}
      <div className={cardCls}>
        {secTitle("Seção Repertório")}
        {txt("repertoire_kicker", "Rótulo")}
        {txt("repertoire_title", "Título")}
        {txt("repertoire_desc", "Descrição", true)}
        <ImageField label="Imagem (repertório)" k="img_repertoire" />
      </div>

      {/* COMO FUNCIONA */}
      <div className={cardCls}>
        {secTitle("Seção Como Funciona")}
        {txt("how_title", "Título")}
        {txt("how_sub", "Subtítulo", true)}
        <Label className="text-[11px] text-zinc-500 block pt-1">Passos</Label>
        {tdList("timeline", "Passo")}
      </div>

      {/* SCORE */}
      <div className={cardCls}>
        {secTitle("Seção Score")}
        {txt("score_kicker", "Rótulo")}
        {txt("score_title", "Título")}
        {txt("score_desc", "Descrição", true)}
        {txt("score_cta", "Texto do botão")}
      </div>

      {/* RECURSOS */}
      <div className={cardCls}>
        {secTitle("Seção Recursos")}
        {txt("features_title", "Título")}
        {txt("features_sub", "Subtítulo")}
        <Label className="text-[11px] text-zinc-500 block pt-1">Cards de recursos</Label>
        {iconTextList("features", "Recurso")}
      </div>

      {/* PLANOS */}
      <div className={cardCls}>
        {secTitle("Seção Planos")}
        {txt("planos_title", "Título")}
        {txt("planos_sub", "Subtítulo")}
        <div><Label className="text-[11px] text-zinc-500">Plano recomendado</Label>
          <select data-testid="lp-recommended" value={c.recommended_plan || ""} onChange={(e) => s("recommended_plan", e.target.value)} className="block w-full bg-[#09090B] border border-white/10 rounded-xl mt-1 h-9 px-2 text-sm text-zinc-200">
            {plans.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* SEGURANÇA / PROVA SOCIAL */}
      <div className={cardCls}>
        {secTitle("Seção Segurança / Prova social")}
        <Label className="text-[11px] text-zinc-500 block">Cards de destaque</Label>
        {iconTextList("seguranca_cards", "Card")}
      </div>

      {/* FAQ */}
      <div className={cardCls}>
        <div className="flex items-center justify-between">
          {secTitle(`FAQ ${(c.faq || []).length > 0 ? `(${c.faq.length})` : "(usando padrão)"}`)}
          <Button size="sm" variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1" onClick={() => s("faq", [...(c.faq || []), { q: "", a: "" }])}><Plus className="h-3.5 w-3.5" />Pergunta</Button>
        </div>
        <div className="space-y-2">
          {(c.faq || []).map((f, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex-1 space-y-1">
                <Input value={f.q} onChange={(e) => { const n = [...c.faq]; n[i] = { ...n[i], q: e.target.value }; s("faq", n); }} placeholder="Pergunta" className="bg-[#09090B] border-white/10 rounded-xl" />
                <Textarea value={f.a} onChange={(e) => { const n = [...c.faq]; n[i] = { ...n[i], a: e.target.value }; s("faq", n); }} placeholder="Resposta" className="bg-[#09090B] border-white/10 rounded-xl" />
              </div>
              <button onClick={() => s("faq", c.faq.filter((_, j) => j !== i))} className="h-8 w-8 rounded-xl border border-white/10 text-zinc-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {(c.faq || []).length === 0 && <p className="text-[11px] text-zinc-600">Sem itens — a landing usa o FAQ padrão. Adicione perguntas para personalizar.</p>}
        </div>
      </div>

      {/* CTA FINAL & FOOTER */}
      <div className={cardCls}>
        {secTitle("CTA Final & Rodapé")}
        {txt("final_title", "Título do CTA final")}
        {txt("final_sub", "Subtítulo do CTA final", true)}
        {txt("footer_desc", "Descrição do rodapé", true)}
      </div>

      {/* LINKS */}
      <div className={cardCls}>
        {secTitle("Links (redes & lojas)")}
        <div className="grid grid-cols-2 gap-2">
          {[["instagram", "Instagram"], ["facebook", "Facebook"], ["youtube", "YouTube"], ["tiktok_url", "TikTok"], ["play_store", "Google Play"], ["app_store", "App Store"]].map(([k, l]) => (
            <div key={k}><Label className="text-[11px] text-zinc-500">{l}</Label><Input value={c[k] || ""} onChange={(e) => s(k, e.target.value)} placeholder="https://..." className={inp} /></div>
          ))}
        </div>
      </div>

      {/* SEO & VÍDEO */}
      <div className={cardCls}>
        {secTitle("SEO & Vídeo")}
        {txt("seo_title", "SEO title")}
        {txt("seo_description", "SEO description", true)}
        {txt("video_url", "URL do vídeo (MP4/WebM)")}
      </div>

      {/* ANALYTICS */}
      <div className={cardCls}>
        {secTitle("Analytics & Pixels")}
        <div className="grid grid-cols-2 gap-2">
          {ANALYTICS.map(([k, l]) => (
            <div key={k}><Label className="text-[11px] text-zinc-500">{l}</Label><Input data-testid={`lp-an-${k}`} value={(c.analytics || {})[k] || ""} onChange={(e) => s("analytics", { ...(c.analytics || {}), [k]: e.target.value })} className={inp} /></div>
          ))}
        </div>
      </div>
    </div>
  );
}
